<template>
  <div class="search">
    <div class="container">
      <!-- Header -->
      <header class="sr-head reveal">
        <router-link to="/profil" class="sr-back">← Retour au profil</router-link>
        <p class="eyebrow">Ton petit musée</p>
        <h1 class="sr-title">⭐ Mes favoris</h1>
        <p class="sr-sub">
          Cherche, trie et filtre les blagues que tu as mises de côté.
        </p>
      </header>

      <!-- Search bar -->
      <div class="sr-searchbar reveal">
        <span class="sr-searchbar__icon" aria-hidden="true">🔍</span>
        <input
          v-model="query"
          type="search"
          class="sr-searchbar__input"
          placeholder="Cherche dans tes favoris…"
          aria-label="Rechercher dans mes favoris"
          @input="onQueryInput"
        />
        <button
          v-if="query"
          class="sr-searchbar__clear"
          aria-label="Effacer la recherche"
          @click="clearQuery"
        >
          ×
        </button>
      </div>

      <!-- Sort -->
      <div class="sr-sort reveal">
        <label class="sr-sort__label" for="sr-sort-select">Trier par</label>
        <div class="sr-sort__field">
          <select
            id="sr-sort-select"
            v-model="orderBy"
            class="sr-sort__select"
            aria-label="Trier les résultats"
          >
            <option
              v-for="opt in sortOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.emoji }}  {{ opt.label }}
            </option>
          </select>
          <span class="sr-sort__chevron" aria-hidden="true">▾</span>
        </div>
      </div>

      <!-- Tag filters -->
      <section class="sr-filters reveal">
        <p class="sr-filters__hint">
          Clique une étiquette pour l'<strong>inclure</strong> (toutes les
          étiquettes cochées doivent être présentes), re-clique pour l'<strong>exclure</strong>,
          encore une fois pour la retirer. Le compteur montre combien de tes favoris
          restent avec la sélection courante.
        </p>

        <div
          v-for="group in tagGroups"
          :key="group.axis"
          class="sr-axis"
        >
          <div class="sr-axis__head">
            <span class="sr-axis__emoji" aria-hidden="true">{{ group.emoji }}</span>
            <h2 class="sr-axis__title">{{ group.title }}</h2>
          </div>
          <div class="sr-tags" :class="{ 'sr-tags--loading': facetsLoading }">
            <button
              v-for="tag in group.tags"
              :key="tag.slug"
              class="sr-tag"
              :class="{
                'sr-tag--on': tagState(tag.slug) === 'include',
                'sr-tag--excluded': tagState(tag.slug) === 'exclude',
                'sr-tag--sensitive': tag.isSensitive,
                'sr-tag--empty': isDeadEnd(tag),
              }"
              :disabled="isDeadEnd(tag)"
              :aria-pressed="tagState(tag.slug) === 'include'"
              :title="tagTitle(tag)"
              @click="cycleTag(tag.slug)"
            >
              <span v-if="tagState(tag.slug) === 'include'" class="sr-tag__mark" aria-hidden="true">✓</span>
              <span v-else-if="tagState(tag.slug) === 'exclude'" class="sr-tag__mark" aria-hidden="true">✕</span>
              <span class="sr-tag__label">{{ tag.label }}</span>
              <span v-if="tag.jokeCount != null" class="sr-tag__count">{{ tag.jokeCount }}</span>
            </button>
          </div>
        </div>

        <button
          v-if="activeFilterCount"
          class="sr-clear-tags"
          @click="clearTags"
        >
          ✕ Retirer {{ activeFilterCount }} filtre{{ activeFilterCount > 1 ? 's' : '' }}
        </button>
      </section>

      <!-- Results -->
      <section ref="results" class="sr-results">
        <!-- Loading (fresh) -->
        <div v-if="loading && !jokes.length" class="sr-state">
          <span class="sr-spin">🍌</span>
          <p>On fouille ton petit musée…</p>
        </div>

        <!-- Empty: no favorites at all (no active filter) -->
        <div v-else-if="!loading && !jokes.length && !hasActiveFilters" class="sr-state">
          <div class="sr-state__emoji">🍌</div>
          <p>Tu n'as pas encore de favori. Ajoute des blagues en cliquant sur l'étoile ⭐&nbsp;!</p>
          <router-link to="/" class="sr-empty-cta">Parcourir les blagues</router-link>
        </div>

        <!-- Empty: filters active but nothing matches -->
        <div v-else-if="!loading && !jokes.length" class="sr-state">
          <div class="sr-state__emoji">🫥</div>
          <p>Aucun favori ne correspond à ces filtres. Essaie d'autres mots ou tags.</p>
        </div>

        <!-- List -->
        <template v-else>
          <p class="sr-count">
            {{ total }} favori{{ total > 1 ? 's' : '' }}
            <span v-if="pageCount > 1" class="sr-count__page">• page {{ page }}/{{ pageCount }}</span>
          </p>
          <div class="sr-list" :class="{ 'sr-list--busy': loading }">
            <JokeCard
              v-for="joke in jokes"
              :key="joke.id"
              :joke="joke"
            />
          </div>

          <!-- Numbered pagination -->
          <nav
            v-if="pageCount > 1"
            class="sr-pager"
            role="navigation"
            aria-label="Pagination des résultats"
          >
            <button
              class="sr-pager__btn sr-pager__nav"
              :disabled="page <= 1 || loading"
              aria-label="Page précédente"
              @click="goToPage(page - 1)"
            >
              ‹<span class="sr-pager__navlabel"> Précédent</span>
            </button>

            <ul class="sr-pager__pages">
              <li
                v-for="(item, i) in pageItems"
                :key="item.type === 'gap' ? 'gap-' + i : 'p-' + item.value"
              >
                <span v-if="item.type === 'gap'" class="sr-pager__gap" aria-hidden="true">…</span>
                <button
                  v-else
                  class="sr-pager__btn sr-pager__page"
                  :class="{ 'sr-pager__page--active': item.value === page }"
                  :aria-current="item.value === page ? 'page' : undefined"
                  :aria-label="'Page ' + item.value"
                  :disabled="loading"
                  @click="goToPage(item.value)"
                >
                  {{ item.value }}
                </button>
              </li>
            </ul>

            <button
              class="sr-pager__btn sr-pager__nav"
              :disabled="page >= pageCount || loading"
              aria-label="Page suivante"
              @click="goToPage(page + 1)"
            >
              <span class="sr-pager__navlabel">Suivant </span>›
            </button>
          </nav>
          <p v-else class="sr-end">🍌 T'as tout épluché.</p>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import JokeCard from '@/components/JokeCard.vue'
import {
  SEARCH_JOKES_QUERY,
  SEARCH_JOKES_COUNT_QUERY,
  TAG_FACETS_QUERY,
} from '@/graphql/jokes'

const PAGE = 20
const DEBOUNCE_MS = 300

const AXIS_META = {
  ton: { title: 'Ton', emoji: '🎭', order: 0 },
  theme: { title: 'Thème', emoji: '🏷️', order: 1 },
}

export default {
  name: 'ProfileFavoritesView',
  components: { JokeCard },
  data() {
    return {
      query: '',
      debouncedQuery: '',
      includeTags: [],
      excludeTags: [],
      orderBy: 'RECENT',
      tags: [],
      facetsLoading: false,
      jokes: [],
      total: 0,
      page: 1,
      loading: false,
      debounceTimer: null,
      listToken: null,
      facetToken: null,
      sortOptions: [
        { value: 'RECENT', label: 'Récent', emoji: '🆕' },
        { value: 'ANCIEN', label: 'Ancien', emoji: '📜' },
        { value: 'TOP', label: 'Top', emoji: '🏆' },
        { value: 'FLOP', label: 'Flop', emoji: '💩' },
        { value: 'TOP_LEGACY', label: 'Top légende', emoji: '👑' },
        { value: 'FLOP_LEGACY', label: 'Flop légende', emoji: '🥀' },
        { value: 'ID', label: 'Par n°', emoji: '🔢' },
      ],
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / PAGE))
    },
    activeFilterCount() {
      return this.includeTags.length + this.excludeTags.length
    },
    // True when the user has narrowed the view with a keyword or a tag filter.
    // Used to distinguish "no favorites at all" from "no favorites for these filters".
    hasActiveFilters() {
      return !!this.debouncedQuery || this.activeFilterCount > 0
    },
    tagGroups() {
      const byAxis = {}
      for (const tag of this.tags || []) {
        const axis = tag.axis || 'theme'
        if (!byAxis[axis]) byAxis[axis] = []
        byAxis[axis].push(tag)
      }
      return Object.keys(byAxis)
        .map((axis) => {
          const meta = AXIS_META[axis] || { title: axis, emoji: '🏷️', order: 99 }
          return {
            axis,
            title: meta.title,
            emoji: meta.emoji,
            order: meta.order,
            tags: byAxis[axis].slice().sort((a, b) => a.label.localeCompare(b.label, 'fr')),
          }
        })
        .sort((a, b) => a.order - b.order)
    },
    /**
     * Numbered pagination model: first page, last page, current page ± 1,
     * with '…' gaps in between. e.g. [1, …, 4, 5, 6, …, 20].
     */
    pageItems() {
      const total = this.pageCount
      const current = this.page
      const items = []
      if (total <= 7) {
        for (let p = 1; p <= total; p++) items.push({ type: 'page', value: p })
        return items
      }
      const pages = new Set([1, total, current, current - 1, current + 1])
      const sorted = [...pages]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b)
      let prev = 0
      for (const p of sorted) {
        if (p - prev > 1) items.push({ type: 'gap' })
        items.push({ type: 'page', value: p })
        prev = p
      }
      return items
    },
  },
  watch: {
    debouncedQuery() {
      this.resetAndReload()
    },
    includeTags() {
      this.resetAndReload()
    },
    excludeTags() {
      this.resetAndReload()
    },
    orderBy() {
      // Sort doesn't change facet counts nor the total — only reorder the list.
      this.page = 1
      this.fetchList()
    },
  },
  created() {
    // Deep-linking: pre-select tags / keyword from the URL so tag links
    // elsewhere can jump into a pre-filtered favorites view.
    const q = this.$route.query
    const preset = q.include || q.tag
    if (preset) {
      const slugs = Array.isArray(preset) ? preset : String(preset).split(',')
      this.includeTags = slugs.map((s) => s.trim()).filter(Boolean)
    }
    if (q.exclude) {
      const slugs = Array.isArray(q.exclude) ? q.exclude : String(q.exclude).split(',')
      this.excludeTags = slugs.map((s) => s.trim()).filter(Boolean)
    }
    if (q.q) {
      this.query = String(q.q)
      this.debouncedQuery = String(q.q)
    }
  },
  mounted() {
    this.fetchFacets()
    this.fetchCountThenList()
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  },
  methods: {
    onQueryInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.debouncedQuery = this.query.trim()
      }, DEBOUNCE_MS)
    },
    clearQuery() {
      this.query = ''
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debouncedQuery = ''
    },
    // --- Tag state (tri-state: neutral / include / exclude) ---
    tagState(slug) {
      if (this.includeTags.includes(slug)) return 'include'
      if (this.excludeTags.includes(slug)) return 'exclude'
      return 'neutral'
    },
    isDeadEnd(tag) {
      // A tag whose contextual count is 0 leads nowhere — grey it out, unless
      // it's already part of the active selection (must stay togglable to undo).
      return tag.jokeCount === 0 && this.tagState(tag.slug) === 'neutral'
    },
    tagTitle(tag) {
      const st = this.tagState(tag.slug)
      if (st === 'include') return 'Inclus — clique pour exclure'
      if (st === 'exclude') return 'Exclu — clique pour retirer'
      if (this.isDeadEnd(tag)) return 'Aucun favori avec la sélection courante'
      return 'Clique pour inclure'
    },
    cycleTag(slug) {
      // neutral -> include -> exclude -> neutral
      const st = this.tagState(slug)
      if (st === 'neutral') {
        this.includeTags = [...this.includeTags, slug]
      } else if (st === 'include') {
        this.includeTags = this.includeTags.filter((s) => s !== slug)
        this.excludeTags = [...this.excludeTags, slug]
      } else {
        this.excludeTags = this.excludeTags.filter((s) => s !== slug)
      }
    },
    clearTags() {
      this.includeTags = []
      this.excludeTags = []
    },
    // --- Variables ---
    // Restrict every request to the current user's favorites. This is the only
    // functional difference with the global Search view.
    filterVariables() {
      return {
        query: this.debouncedQuery || null,
        includeTags: this.includeTags.length ? this.includeTags : null,
        excludeTags: this.excludeTags.length ? this.excludeTags : null,
        favoritesOnly: true,
      }
    },
    // --- Orchestration ---
    // Any filter change (keyword / tags): reset to page 1, refetch facets,
    // count, and the list.
    resetAndReload() {
      this.page = 1
      this.fetchFacets()
      this.fetchCountThenList()
    },
    fetchFacets() {
      this.facetsLoading = true
      const token = Symbol('facets')
      this.facetToken = token
      this.$apollo
        .query({
          query: TAG_FACETS_QUERY,
          fetchPolicy: 'network-only',
          variables: this.filterVariables(),
        })
        .then((res) => {
          if (this.facetToken !== token) return
          this.tags = (res.data && res.data.tagFacets) || []
        })
        .catch((err) => {
          if (this.facetToken !== token) return
          console.error('Facets failed:', err)
        })
        .finally(() => {
          if (this.facetToken !== token) return
          this.facetsLoading = false
        })
    },
    // Fetch the total first (drives page count), then the current page's list.
    fetchCountThenList() {
      this.$apollo
        .query({
          query: SEARCH_JOKES_COUNT_QUERY,
          fetchPolicy: 'network-only',
          variables: this.filterVariables(),
        })
        .then((res) => {
          const n = res.data && res.data.searchJokesCount
          this.total = typeof n === 'number' ? n : 0
        })
        .catch((err) => {
          console.error('Count failed:', err)
          this.total = 0
        })
      this.fetchList()
    },
    fetchList() {
      this.loading = true
      const token = Symbol('list')
      this.listToken = token
      const offset = (this.page - 1) * PAGE
      this.$apollo
        .query({
          query: SEARCH_JOKES_QUERY,
          fetchPolicy: 'network-only',
          variables: {
            ...this.filterVariables(),
            count: PAGE,
            offset,
            orderBy: this.orderBy,
          },
        })
        .then((res) => {
          if (this.listToken !== token) return
          this.jokes = (res.data && res.data.searchJokes) || []
        })
        .catch((err) => {
          if (this.listToken !== token) return
          console.error('Search failed:', err)
          this.jokes = []
        })
        .finally(() => {
          if (this.listToken !== token) return
          this.loading = false
        })
    },
    goToPage(p) {
      if (p < 1 || p > this.pageCount || p === this.page || this.loading) return
      this.page = p
      this.fetchList()
      this.$nextTick(() => {
        const el = this.$refs.results
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
  },
}
</script>

<style scoped>
.search {
  padding: 40px 0 80px;
}

/* Header */
.sr-head {
  text-align: center;
  margin-bottom: 26px;
}
.sr-back {
  display: inline-block;
  margin-bottom: 14px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-700);
  text-decoration: none;
}
.sr-back:hover { color: var(--ink-900); text-decoration: underline; }
.sr-title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2.4rem, 6vw, 3.6rem);
  color: var(--ink-900);
  margin: 4px 0 12px;
  letter-spacing: -0.02em;
}
.sr-sub {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.12rem;
  color: var(--ink-500);
  max-width: 520px;
  margin: 0 auto;
}

/* Search bar */
.sr-searchbar {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-pop);
  padding: 4px 6px 4px 18px;
  margin-bottom: 20px;
  transition: box-shadow 0.15s var(--ease-out), transform 0.15s var(--ease-bounce);
}
.sr-searchbar:focus-within {
  transform: translate(-1px, -1px);
  box-shadow: 6px 6px 0 var(--border-c);
}
.sr-searchbar__icon {
  font-size: 1.15rem;
  flex-shrink: 0;
  margin-right: 8px;
}
.sr-searchbar__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 1.05rem;
  color: var(--ink-900);
  padding: 12px 4px;
}
.sr-searchbar__input::placeholder { color: var(--ink-300); }
/* hide native search clear (we render our own) */
.sr-searchbar__input::-webkit-search-cancel-button { display: none; }
.sr-searchbar__clear {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: none;
  background: var(--banana-100);
  color: var(--ink-900);
  border-radius: 50%;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s var(--ease-bounce);
}
.sr-searchbar__clear:hover { background: var(--banana-300); transform: scale(1.1); }

/* Sort */
.sr-sort {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}
.sr-sort__label {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-900);
}
.sr-sort__field {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.sr-sort__select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--ink-900);
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  box-shadow: 2px 2px 0 var(--border-c);
  padding: 9px 38px 9px 16px;
  cursor: pointer;
  outline: none;
  transition: transform 0.12s var(--ease-bounce), box-shadow 0.15s;
}
.sr-sort__select:hover {
  transform: translate(-1px, -1px);
  box-shadow: 3px 3px 0 var(--border-c);
}
.sr-sort__select:focus-visible {
  transform: translate(-1px, -1px);
  box-shadow: 4px 4px 0 var(--banana-500);
}
.sr-sort__chevron {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 0.9rem;
  color: var(--ink-900);
}

/* Filters */
.sr-filters {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: 20px 22px;
  margin-bottom: 34px;
}
.sr-filters__hint {
  font-family: var(--font-body);
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--ink-500);
  margin: 0 0 16px;
}
.sr-filters__hint strong { color: var(--ink-800); }
.sr-axis + .sr-axis { margin-top: 18px; padding-top: 16px; border-top: 1.5px dashed var(--line); }
.sr-axis__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.sr-axis__emoji { font-size: 1.1rem; }
.sr-axis__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.05rem;
  color: var(--ink-900);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sr-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  transition: opacity 0.15s;
}
.sr-tags--loading { opacity: 0.55; }
.sr-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.88rem;
  padding: 6px 12px;
  border: 1.5px solid var(--border-c);
  background: var(--cream);
  color: var(--ink-800);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: transform 0.12s var(--ease-bounce), background 0.15s, box-shadow 0.15s, opacity 0.15s;
}
.sr-tag:hover:not(:disabled) { transform: translateY(-2px); background: var(--banana-100); }
.sr-tag--on {
  background: var(--banana-500);
  color: var(--ink-900);
  box-shadow: 2px 2px 0 var(--border-c);
}
.sr-tag--excluded {
  background: color-mix(in srgb, var(--bruise) 16%, var(--cream));
  color: var(--bruise);
  border-color: var(--bruise);
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
}
.sr-tag--excluded:hover:not(:disabled) { background: color-mix(in srgb, var(--bruise) 24%, var(--cream)); }
.sr-tag--empty {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--cream);
  color: var(--ink-300);
  border-color: var(--ink-300);
}
.sr-tag--empty:hover { transform: none; background: var(--cream); }
.sr-tag__mark {
  font-size: 0.78em;
  line-height: 1;
}
.sr-tag__count {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ink-500);
  background: color-mix(in srgb, var(--ink-900) 8%, transparent);
  border-radius: var(--r-pill);
  padding: 1px 7px;
  min-width: 20px;
  text-align: center;
}
.sr-tag--on .sr-tag__count { color: var(--ink-900); background: rgba(0, 0, 0, 0.12); }
.sr-tag--excluded .sr-tag__count { color: var(--bruise); background: color-mix(in srgb, var(--bruise) 12%, transparent); }

.sr-clear-tags {
  margin-top: 16px;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--bruise);
  background: transparent;
  border: 1.5px solid var(--bruise);
  border-radius: var(--r-pill);
  padding: 5px 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.sr-clear-tags:hover { background: var(--bruise); color: #fff; }

/* Results */
.sr-count {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-500);
  margin: 0 0 16px;
}
.sr-count__page { color: var(--ink-800); }
.sr-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: opacity 0.15s;
}
.sr-list--busy { opacity: 0.5; }

.sr-state {
  text-align: center;
  padding: 56px 20px;
  color: var(--ink-500);
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.12rem;
}
.sr-state__emoji { font-size: 3rem; margin-bottom: 8px; }
.sr-state p { margin: 0; }
.sr-empty-cta {
  display: inline-block;
  margin-top: 18px;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 0.95rem;
  font-style: normal;
  color: var(--banana-400);
  background: var(--choco);
  border-radius: var(--r-pill);
  box-shadow: 2px 2px 0 var(--banana-500);
  padding: 10px 20px;
  text-decoration: none;
  transition: transform 0.12s var(--ease-bounce), box-shadow 0.15s;
}
.sr-empty-cta:hover {
  transform: translate(-1px, -1px);
  box-shadow: 3px 3px 0 var(--banana-500);
}
.sr-spin {
  display: inline-block;
  font-size: 2.4rem;
  animation: sr-spin 1.1s linear infinite;
}
@keyframes sr-spin { to { transform: rotate(360deg); } }

/* Numbered pagination */
.sr-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 36px;
}
.sr-pager__pages {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 100%;
  overflow-x: auto;
}
.sr-pager__btn {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-900);
  background: var(--surface);
  border: 1.5px solid var(--border-c);
  border-radius: var(--r-pill);
  padding: 7px 13px;
  min-width: 40px;
  cursor: pointer;
  transition: transform 0.12s var(--ease-bounce), background 0.15s, box-shadow 0.15s, opacity 0.15s;
}
.sr-pager__btn:hover:not(:disabled) {
  transform: translate(-1px, -1px);
  background: var(--banana-100);
  box-shadow: 2px 2px 0 var(--border-c);
}
.sr-pager__btn:focus-visible {
  outline: 3px solid var(--banana-500);
  outline-offset: 2px;
}
.sr-pager__btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
.sr-pager__page--active,
.sr-pager__page--active:disabled {
  background: var(--choco);
  color: var(--banana-400);
  box-shadow: 2px 2px 0 var(--banana-500);
  opacity: 1;
  cursor: default;
}
.sr-pager__page--active:hover { transform: none; background: var(--choco); }
.sr-pager__gap {
  display: inline-block;
  padding: 0 4px;
  color: var(--ink-500);
  font-weight: 700;
  user-select: none;
}
.sr-pager__nav { padding: 7px 15px; }

.sr-end {
  text-align: center;
  margin-top: 36px;
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.05rem;
  color: var(--ink-500);
}

@media (max-width: 560px) {
  .search { padding: 32px 0 60px; }
  .sr-filters { padding: 16px 16px; }
  .sr-searchbar__input { font-size: 1rem; }
  .sr-sort__select { flex: 1; }
  .sr-sort__field { flex: 1; }
  /* Collapse verbose nav labels so the pager never overflows the page */
  .sr-pager__navlabel { display: none; }
  .sr-pager__nav { padding: 7px 12px; min-width: 40px; }
  .sr-pager__btn { padding: 7px 11px; min-width: 38px; }
}
</style>
